import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Tag,
  Input,
  Modal,
  Form,
  message,
  Typography,
  Grid,
  Select,
  Switch,
  Card,
  Row,
  Col,
  Statistic,
  Avatar,
  Popconfirm,
  Divider,
  Alert
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  CheckOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FilterOutlined,
  MailOutlined,
  PhoneOutlined,
  WechatOutlined,
  KeyOutlined,
  HomeOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons'
import ImageUpload from '@/components/ImageUpload'
import { userApi, roleApi, merchantRoomApi, venueAdminApi } from '@/services/api'

const { Title, Text } = Typography
const { Search } = Input
const { useBreakpoint } = Grid
const { Option } = Select

interface UserStats {
  total: number
  active: number
  inactive: number
  verified: number
  pending: number
  superUsers: number
}

interface User {
  id: number
  username: string
  email: string
  phone?: string
  hashed_password?: string
  nickname?: string
  avatar_url?: string
  bio?: string
  birthday?: string
  gender?: string
  status: 'active' | 'inactive' | 'locked' | 'deleted'
  is_active: boolean
  is_verified: boolean
  max_concurrent_sessions?: number
  device_fingerprints?: any[]
  real_name?: string
  id_card_number?: string
  kyc_status: 'unverified' | 'pending' | 'approved' | 'rejected'
  kyc_verified_at?: string
  extra_metadata?: Record<string, any>
  created_at: string
  updated_at: string
  deleted_at?: string
  last_login_at?: string
  wechat_union_id?: string
  wechat_nickname?: string
  wechat_avatar_url?: string
  wechat_bound_at?: string
  personality_tags?: string[]
  profession?: string
  interest_tags?: string[]
  region?: string
  points?: number
  total_points_purchased?: number
  highlight_balance?: number
  stardust_balance?: number
  total_highlight_consumed?: number
  total_stardust_consumed?: number
  achievement_points?: number
  total_games_played?: number
  is_deleted: boolean
  uuid: string
  // 兼容旧字段
  role?: string
  roles?: string[]
  is_superuser?: boolean
}

interface TagOption {
  value: string
  label: string
}


const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([])
  const [roles, setRoles] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [rolesLoading, setRolesLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [roleFilter, setRoleFilter] = useState<string>('')
  const [userTypeFilter, setUserTypeFilter] = useState<string>('')  // 'user' | 'npc' | ''
  const [includeDeleted, setIncludeDeleted] = useState(false)
  // 分页状态
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [profileModalVisible, setProfileModalVisible] = useState(false)
  const [viewingUser, setViewingUser] = useState<any | null>(null)
  const [form] = Form.useForm()
  const screens = useBreakpoint()
  const [stats, setStats] = useState<UserStats>({
    total: 0,
    active: 0,
    inactive: 0,
    verified: 0,
    pending: 0,
    superUsers: 0
  })

  // Avatar upload states
  const [avatarUrl, setAvatarUrl] = useState<string>('')

  // Binding management states
  const [bindEmailModalVisible, setBindEmailModalVisible] = useState(false)
  const [bindPhoneModalVisible, setBindPhoneModalVisible] = useState(false)
  const [bindWechatModalVisible, setBindWechatModalVisible] = useState(false)
  const [bindingLoading, setBindingLoading] = useState(false)

  // Password management states
  const [passwordModalVisible, setPasswordModalVisible] = useState(false)
  const [passwordUser, setPasswordUser] = useState<User | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)

  // Tag options loaded from API
  const [personalityTagOptions, setPersonalityTagOptions] = useState<TagOption[]>([])
  const [interestTagOptions, setInterestTagOptions] = useState<TagOption[]>([])
  const [tagsLoading, setTagsLoading] = useState(false)

  // Venue binding states
  const [venueBindModalVisible, setVenueBindModalVisible] = useState(false)
  const [bindingVenueUser, setBindingVenueUser] = useState<User | null>(null)
  const [merchants, setMerchants] = useState<any[]>([])
  const [venues, setVenues] = useState<any[]>([])
  const [selectedMerchantId, setSelectedMerchantId] = useState<number | null>(null)
  const [merchantsLoading, setMerchantsLoading] = useState(false)
  const [venuesLoading, setVenuesLoading] = useState(false)
  const [venueBindForm] = Form.useForm()

  // 加载默认标签选项
  useEffect(() => {
    setTagsLoading(true)
    try {
      // 设置默认选项
      setPersonalityTagOptions([
        { value: '外向', label: '外向' },
        { value: '内向', label: '内向' },
        { value: '理性', label: '理性' },
        { value: '感性', label: '感性' },
        { value: '幽默', label: '幽默' },
        { value: '严谨', label: '严谨' },
      ])
      setInterestTagOptions([
        { value: '游戏', label: '游戏' },
        { value: '电影', label: '电影' },
        { value: '运动', label: '运动' },
        { value: '音乐', label: '音乐' },
      ])
    } finally {
      setTagsLoading(false)
    }
  }, [])

  const isMobile = !screens.md


  useEffect(() => {
    fetchUsers()
    fetchRoles()
  }, [searchText, statusFilter, roleFilter, userTypeFilter, includeDeleted, page, pageSize])

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const response = await userApi.getUsers({
        page: page,
        page_size: pageSize,
        search: searchText || undefined,
        role: roleFilter || undefined,
        is_active: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
        is_verified: statusFilter === 'verified' ? true : undefined,
        kyc_status: statusFilter === 'pending' ? 'pending' : undefined,
        include_deleted: includeDeleted,
      })

      const userList = response.data.items || []
      const totalCount = response.data.total || 0

      // 本地过滤：用户类型（如果API不支持）
      let filteredList = userTypeFilter
        ? userList.filter((user: User) => {
          const isNpc = user.extra_metadata?.is_npc === true
          if (userTypeFilter === 'npc') return isNpc
          if (userTypeFilter === 'user') return !isNpc
          return true
        })
        : userList

      setUsers(filteredList)
      setTotal(totalCount)

      // 获取统计数据（独立调用，不受分页影响）
      try {
        const statsResponse = await userApi.getStats({
          search: searchText || undefined,
          role: roleFilter || undefined,
        })
        if (statsResponse.data) {
          setStats({
            total: statsResponse.data.total || totalCount,
            active: statsResponse.data.active || 0,
            inactive: statsResponse.data.inactive || 0,
            verified: statsResponse.data.verified || 0,
            pending: statsResponse.data.pending || 0,
            superUsers: statsResponse.data.super_users || 0,
          })
        }
      } catch (statsError) {
        console.error('获取统计数据失败:', statsError)
        // 如果统计API失败，使用当前页数据作为备选
        updateStats(filteredList, totalCount)
      }
    } catch (error) {
      message.error('获取用户列表失败')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const updateStats = (userList: User[], totalCount: number) => {
    const newStats = {
      total: totalCount,  // ✅ 使用数据库返回的总数
      active: userList.filter(u => u.is_active).length,
      inactive: userList.filter(u => !u.is_active).length,
      verified: userList.filter(u => u.kyc_status === 'approved').length,
      pending: userList.filter(u => u.kyc_status === 'pending').length,
      superUsers: userList.filter(u => u.is_superuser).length
    }
    setStats(newStats)
  }

  const fetchRoles = async () => {
    setRolesLoading(true)
    try {
      const response = await roleApi.getRoles({
        page: 1,
        page_size: 100,
      })
      setRoles(response.data.items || [])
    } catch (error) {
      console.error('获取角色列表失败:', error)
      // 如果 API 失败，使用默认角色列表作为备份
      // 角色格式: [前缀_role_code] 角色名称
      setRoles([
        { role_code: 'super_admin', name: '超级管理员', prefix: 'admin', category: '管理员' },
        { role_code: 'platform_admin', name: '平台管理员', prefix: 'admin', category: '管理员' },
        { role_code: 'admin', name: '管理员', prefix: 'admin', category: '管理员' },
        { role_code: 'merchant_admin', name: '商家管理员', prefix: 'admin', category: '管理员' },
        { role_code: 'venue_admin', name: '场馆管理员', prefix: 'admin', category: '管理员' },
        { role_code: 'merchant', name: '商家', prefix: 'merchant', category: '商户' },
        { role_code: 'playwright', name: '编剧', prefix: 'content', category: '内容' },
        { role_code: 'reviewer', name: '审核员', prefix: 'content', category: '内容' },
        { role_code: 'actor', name: '演员', prefix: 'actor', category: '演出' },
        { role_code: 'advertiser', name: '广告主', prefix: 'ads', category: '广告' },
        { role_code: 'agent_developer', name: 'Agent开发者', prefix: 'dev', category: '开发' },
        { role_code: 'player', name: '玩家', prefix: 'user', category: '用户' },
      ])
    } finally {
      setRolesLoading(false)
    }
  }

  const handleSearch = (value: string) => {
    setSearchText(value)
    fetchUsers()
  }

  const handleAdd = () => {
    setEditingUser(null)
    form.resetFields()
    setAvatarUrl('')
    setModalVisible(true)
  }

  const handleEdit = (user: User) => {
    setEditingUser(user)

    // 完整重置表单，避免数据残留
    form.resetFields()

    // 重置头像状态

    // 设置用户数据
    form.setFieldsValue({
      username: user.username,
      email: user.email || '',
      phone: user.phone || '',
      nickname: user.nickname || '',
      role: user.role,
      is_active: user.is_active,
      // 个人信息
      real_name: user.real_name || '',
      gender: user.gender || undefined,
      birthday: user.birthday || undefined,
      region: user.region || '',
      profession: user.profession || '',
      bio: user.bio || '',
      // 标签
      personality_tags: user.personality_tags || [],
      interest_tags: user.interest_tags || [],
    })

    // 设置头像
    if (user.avatar_url) {
      setAvatarUrl(user.avatar_url)
    } else {
      setAvatarUrl('')
    }

    setModalVisible(true)
  }


  // ==================== Binding Management Functions ====================

  const handleSendVerificationCode = async (type: 'email' | 'phone', target: string): Promise<boolean> => {
    if (!viewingUser) return false

    try {
      await userApi.sendVerificationCode(viewingUser.id, {
        type,
        target,
        action: 'bind'
      })
      message.success('验证码已发送')
      return true
    } catch (error) {
      message.error('发送验证码失败')
      console.error(error)
      return false
    }
  }

  const handleBindEmail = async (email: string, code: string) => {
    if (!viewingUser) return

    setBindingLoading(true)
    try {
      await userApi.bindEmail(viewingUser.id, { email, verification_code: code })
      message.success('邮箱绑定成功')
      setBindEmailModalVisible(false)
      fetchUsers()
    } catch (error) {
      message.error('邮箱绑定失败')
      console.error(error)
    } finally {
      setBindingLoading(false)
    }
  }

  const handleBindPhone = async (phone: string, code: string) => {
    if (!viewingUser) return

    setBindingLoading(true)
    try {
      await userApi.bindPhone(viewingUser.id, { phone, verification_code: code })
      message.success('手机绑定成功')
      setBindPhoneModalVisible(false)
      fetchUsers()
    } catch (error) {
      message.error('手机绑定失败')
      console.error(error)
    } finally {
      setBindingLoading(false)
    }
  }

  const handleUnbindPhone = (user: User) => {
    Modal.confirm({
      title: '确认解绑手机',
      content: `确定要解绑用户 ${user.username} 的手机号吗？`,
      onOk: async () => {
        try {
          await userApi.unbindPhone(user.id)
          message.success('手机解绑成功')
          fetchUsers()
        } catch (error) {
          message.error('手机解绑失败')
          console.error(error)
        }
      },
    })
  }

  const handleUnbindWeChat = (user: User) => {
    Modal.confirm({
      title: '确认解绑微信',
      content: `确定要解绑用户 ${user.username} 的微信账号吗？`,
      onOk: async () => {
        try {
          await userApi.unbindWeChat(user.id)
          message.success('微信解绑成功')
          fetchUsers()
        } catch (error) {
          message.error('微信解绑失败')
          console.error(error)
        }
      },
    })
  }

  const handleUnbindEmail = (user: User) => {
    Modal.confirm({
      title: '确认解绑邮箱',
      content: `确定要解绑用户 ${user.username} 的邮箱吗？`,
      onOk: async () => {
        try {
          await userApi.updateUser(user.id, { email: undefined, is_verified: false })
          message.success('邮箱解绑成功')
          setModalVisible(false)
          fetchUsers()
        } catch (error) {
          message.error('邮箱解绑失败')
          console.error(error)
        }
      },
    })
  }

  const handleBindWeChat = async (unionId: string, nickname?: string, avatarUrl?: string) => {
    if (!editingUser) return

    setBindingLoading(true)
    try {
      await userApi.bindWeChat(editingUser.id, {
        union_id: unionId,
        nickname,
        avatar_url: avatarUrl,
      })
      message.success('微信绑定成功')
      setBindWechatModalVisible(false)
      setModalVisible(false)
      fetchUsers()
    } catch (error) {
      message.error('微信绑定失败')
      console.error(error)
    } finally {
      setBindingLoading(false)
    }
  }

  const handleViewProfile = (user: User) => {
    setViewingUser(user)
    setProfileModalVisible(true)
  }

  // ==================== Password Management Functions ====================

  const handleSetPassword = (user: User) => {
    setPasswordUser(user)
    setNewPassword('')
    setPasswordModalVisible(true)
  }

  // ==================== Venue Binding Functions ====================

  // 获取商户列表
  const fetchMerchantsForVenue = async () => {
    setMerchantsLoading(true)
    try {
      const response = await merchantRoomApi.getMerchantsForRooms()
      const merchantList = response.data?.items || []
      setMerchants(merchantList)
    } catch (error) {
      console.error('获取商户列表失败:', error)
      message.error('获取商户列表失败')
    } finally {
      setMerchantsLoading(false)
    }
  }

  // 获取场馆列表（联动）
  const fetchVenuesForBinding = async (merchantId: number) => {
    if (!merchantId) {
      setVenues([])
      return
    }

    setVenuesLoading(true)
    try {
      const response = await merchantRoomApi.getVenuesByMerchant(merchantId)
      const venueList = response.data?.items || []
      setVenues(venueList)
    } catch (error) {
      console.error('获取场馆列表失败:', error)
      message.error('获取场馆列表失败')
      setVenues([])
    } finally {
      setVenuesLoading(false)
    }
  }

  // 打开绑定场馆弹窗
  const handleBindVenue = async (user: User) => {
    // 检查用户角色
    const userRoles = user.roles || []
    const isVenueAdmin = userRoles.includes('venue_admin')

    if (!isVenueAdmin) {
      message.warning('只有场馆管理员才能绑定场馆')
      return
    }

    setBindingVenueUser(user)

    // 从 extra_metadata 读取现有绑定
    const metadata = (user as any).extra_metadata || {}
    const merchantId = metadata.merchant_id
    const venueId = metadata.venue_id

    // 重置表单
    venueBindForm.resetFields()
    setSelectedMerchantId(null)
    setVenues([])

    // 如果有现有绑定，预填充数据
    if (merchantId && venueId) {
      venueBindForm.setFieldsValue({
        merchant_id: merchantId,
        venue_id: venueId,
        venue_name: metadata.venue_name || '',
        venue_address: metadata.venue_address || '',
      })

      // 加载商户和场馆数据
      await fetchMerchantsForVenue()

      // 设置选中的商户并加载场馆列表
      const merchantIdNum = parseInt(merchantId)
      setSelectedMerchantId(merchantIdNum)
      await fetchVenuesForBinding(merchantIdNum)
    } else {
      // 新绑定，只加载商户列表
      await fetchMerchantsForVenue()
    }

    setVenueBindModalVisible(true)
  }

  // 提交绑定场馆
  const handleSubmitVenueBind = async (values: any) => {
    if (!bindingVenueUser) return

    try {
      // 查找选中的场馆信息
      const selectedVenue = venues.find(v => v.id === values.venue_id)

      const bindData = {
        merchant_id: values.merchant_id.toString(),
        venue_id: values.venue_id.toString(),
        venue_name: selectedVenue?.venue_name || values.venue_name || '',
        venue_address: selectedVenue?.address || values.venue_address || '',
      }

      await venueAdminApi.bindVenue(bindingVenueUser.id, bindData)

      message.success('场馆绑定成功')
      setVenueBindModalVisible(false)
      venueBindForm.resetFields()

      await fetchUsers()

      // 清除选择状态
      setSelectedMerchantId(null)
      setVenues([])
    } catch (error: any) {
      console.error('绑定场馆失败:', error)
      message.error(error.response?.data?.message || '绑定场馆失败')
    }
  }

  // 解绑场馆
  const handleUnbindVenue = (user: User) => {
    Modal.confirm({
      title: '确认解绑场馆',
      content: `确定要解绑用户 ${user.username} 的场馆信息吗？`,
      onOk: async () => {
        try {
          await venueAdminApi.unbindVenue(user.id)
          message.success('场馆解绑成功')
          await fetchUsers()
        } catch (error: any) {
          console.error('解绑场馆失败:', error)
          message.error(error.response?.data?.message || '解绑场馆失败')
        }
      },
    })
  }

  // 商户选择变化处理
  const handleMerchantChange = (merchantId: number) => {
    setSelectedMerchantId(merchantId)
    venueBindForm.setFieldsValue({ venue_id: undefined })
    fetchVenuesForBinding(merchantId)
  }

  const handleSetPasswordConfirm = async () => {
    if (!passwordUser) return
    if (!newPassword || newPassword.length < 6) {
      message.warning('密码至少6个字符')
      return
    }

    setPasswordLoading(true)
    try {
      await userApi.adminResetPassword(passwordUser.id, newPassword)
      message.success('密码设置成功')
      setPasswordModalVisible(false)
      fetchUsers()
    } catch (error) {
      message.error('密码设置失败')
      console.error(error)
    } finally {
      setPasswordLoading(false)
    }
  }

  const handleDelete = async (user: User) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除用户 "${user.username}" 吗？`,
      onOk: async () => {
        try {
          await userApi.deleteUser(user.id)
          message.success('用户删除成功')
          fetchUsers()
        } catch (error) {
          message.error('删除用户失败')
          console.error(error)
        }
      },
    })
  }

  const handleSubmit = async (values: any) => {
    try {
      // Include avatar_url in the submission
      const submitData = {
        ...values,
        avatar_url: avatarUrl || undefined,
      }

      if (editingUser) {
        await userApi.updateUser(editingUser.id, submitData)
        message.success('用户更新成功')
      } else {
        await userApi.createUser(submitData)
        message.success('用户创建成功')
      }
      setModalVisible(false)
      setAvatarUrl('')
      fetchUsers()
    } catch (error) {
      message.error(editingUser ? '更新用户失败' : '创建用户失败')
      console.error(error)
    }
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: number) => <Text copyable>{id}</Text>
    },
    {
      title: '用户信息',
      dataIndex: 'username',
      key: 'username',
      fixed: 'left' as const,
      width: 250,
      render: (text: string, record: any) => {
        const isNpc = record.extra_metadata?.is_npc === true
        const scriptName = record.extra_metadata?.script_name

        return (
          <Space>
            {record.avatar_url || record.wechat_avatar_url ? (
              <Avatar src={record.avatar_url || record.wechat_avatar_url} size={40} />
            ) : (
              <Avatar icon={<UserOutlined />} size={40} />
            )}
            <div>
              <div>
                <Button
                  type="link"
                  onClick={() => handleViewProfile(record)}
                  style={{ padding: 0, height: 'auto' }}
                >
                  <Text
                    strong
                    style={{
                      maxWidth: 160,
                      display: 'inline-block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      verticalAlign: 'bottom'
                    }}
                  >
                    {text}
                  </Text>
                </Button>
                {record.is_superuser && <Tag color="red" style={{ marginLeft: 4 }}>超管</Tag>}
                {isNpc && (
                  <Tag color="purple" style={{ marginLeft: 4 }}>
                    NPC{scriptName ? ` · ${scriptName}` : ''}
                  </Tag>
                )}
              </div>
              {record.full_name && (
                <Text type="secondary" style={{ fontSize: 12 }}>{record.full_name}</Text>
              )}
            </div>
          </Space>
        )
      },
    },
    {
      title: '联系方式',
      key: 'contact',
      width: 200,
      responsive: ['md' as const],
      render: (_: any, record: User) => (
        <div>
          {record.email && <div><Text style={{ fontSize: 12 }}>{record.email}</Text></div>}
          {record.phone && <div><Text style={{ fontSize: 12 }}>{record.phone}</Text></div>}
        </div>
      ),
    },
    {
      title: '登录方式',
      key: 'login_methods',
      width: 150,
      responsive: ['lg' as const],
      render: (_: any, record: User) => (
        <Space size={4}>
          {record.email && <Tag color="blue">邮箱</Tag>}
          {record.phone && <Tag color="green">手机</Tag>}
          {record.wechat_union_id && <Tag color="success">微信</Tag>}
        </Space>
      ),
    },
    {
      title: '微信绑定',
      key: 'wechat',
      width: 150,
      responsive: ['xl' as const],
      render: (_: any, record: User) => (
        record.wechat_union_id ? (
          <Space>
            {record.wechat_avatar_url && (
              <Avatar src={record.wechat_avatar_url} size={24} />
            )}
            <Text style={{ fontSize: 12 }}>{record.wechat_nickname || '已绑定'}</Text>
          </Space>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>未绑定</Text>
        )
      ),
    },
    {
      title: '角色',
      key: 'role',
      width: 140,
      render: (_: any, record: any) => {
        const roleConfig: Record<string, { color: string; name: string; prefix: string }> = {
          super_admin: { color: 'red', name: '超级管理员', prefix: 'admin' },
          platform_admin: { color: 'red', name: '平台管理员', prefix: 'admin' },
          admin: { color: 'volcano', name: '管理员', prefix: 'admin' },
          merchant_admin: { color: 'orange', name: '商家管理员', prefix: 'admin' },
          venue_admin: { color: 'gold', name: '场馆管理员', prefix: 'admin' },
          playwright: { color: 'blue', name: '编剧', prefix: 'content' },
          reviewer: { color: 'cyan', name: '审核员', prefix: 'content' },
          merchant: { color: 'green', name: '商家', prefix: 'merchant' },
          actor: { color: 'purple', name: '演员', prefix: 'actor' },
          advertiser: { color: 'magenta', name: '广告主', prefix: 'ads' },
          agent_developer: { color: 'geekblue', name: 'Agent开发者', prefix: 'dev' },
          player: { color: 'default', name: '玩家', prefix: 'user' },
        }
        // 优先使用 role_code，其次是 roles 数组的第一个元素
        const roleCode = record.role_code || (record.roles && record.roles[0]) || 'unknown'
        const config = roleConfig[roleCode] || { color: 'default', name: roleCode, prefix: 'unknown' }
        return (
          <Tag color={config.color}>
            <span style={{ fontSize: 11, opacity: 0.7, marginRight: 4 }}>[{config.prefix}]</span>
            {config.name}
          </Tag>
        )
      },
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_: any, record: User) => (
        <Space direction="vertical" size={0}>
          <Tag color={record.is_active ? 'green' : 'red'}>
            {record.is_active ? '激活' : '禁用'}
          </Tag>
          {record.is_verified && <Tag color="blue" style={{ fontSize: 11 }}>已验证</Tag>}
        </Space>
      ),
    },
    {
      title: 'KYC',
      dataIndex: 'kyc_status',
      key: 'kyc_status',
      width: 90,
      responsive: ['lg' as const],
      render: (status?: string) => {
        if (!status) return '-'
        const kycColors = {
          pending: 'orange',
          approved: 'green',
          rejected: 'red',
        }
        const kycNames = {
          pending: '待审核',
          approved: '已通过',
          rejected: '已拒绝',
        }
        return <Tag color={kycColors[status as keyof typeof kycColors]}>{kycNames[status as keyof typeof kycNames]}</Tag>
      },
    },
    {
      title: '最后登录',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      width: 160,
      responsive: ['xl' as const],
      render: (date?: string) => date ? new Date(date).toLocaleString('zh-CN') : '从未',
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 110,
      responsive: ['lg' as const],
      render: (date: string) => new Date(date).toLocaleDateString('zh-CN'),
    },
    {
      title: '操作',
      dataIndex: 'action',
      fixed: 'right' as const,
      key: 'action',
      render: (_: any, record: User) => {
        // 检查是否是场馆管理员
        const userRoles = record.roles || []
        const isVenueAdmin = userRoles.includes('venue_admin')
        const metadata = (record as any).extra_metadata || {}
        const hasVenueBind = metadata.venue_id

        return (
          <Space size="small">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            >
              编辑
            </Button>
            {isVenueAdmin && (
              <Button
                type="text"
                icon={hasVenueBind ? <EnvironmentOutlined /> : <HomeOutlined />}
                onClick={() => handleBindVenue(record)}
                size="small"
                style={{ color: hasVenueBind ? '#52c41a' : '#1890ff' }}
              >
                {hasVenueBind ? '更新场馆' : '绑定场馆'}
              </Button>
            )}
            <Button
              type="text"
              icon={<KeyOutlined />}
              onClick={() => handleSetPassword(record)}
              size="small"
            >
              设置密码
            </Button>
            <Popconfirm
              title="确认删除"
              description={`确定要删除用户 "${record.username}" 吗？`}
              onConfirm={() => handleDelete(record)}
              okText="确认"
              cancelText="取消"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              >
                删除
              </Button>
            </Popconfirm>
          </Space>
        )
      },
    },
  ]

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <Title level={2}>用户管理</Title>
          <Text type="secondary">管理系统用户和权限</Text>
        </div>
        <Space>
          <Search
            placeholder="搜索用户"
            allowClear
            onSearch={handleSearch}
            style={{ width: isMobile ? 200 : 300 }}
            loading={loading}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            添加用户
          </Button>
        </Space>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} className="mb-6">
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic
              title="总用户数"
              value={stats.total}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic
              title="激活用户"
              value={stats.active}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic
              title="禁用用户"
              value={stats.inactive}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic
              title="已认证"
              value={stats.verified}
              prefix={<CheckOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic
              title="待审核"
              value={stats.pending}
              prefix={<FilterOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic
              title="超级用户"
              value={stats.superUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 筛选器 */}
      <Card className="mb-6">
        <Row gutter={16}>
          <Col xs={24} sm={6}>
            <div>
              <Text strong style={{ marginRight: 8 }}>用户类型:</Text>
              <Select
                value={userTypeFilter}
                onChange={setUserTypeFilter}
                style={{ width: '100%', maxWidth: 150 }}
                allowClear
                placeholder="全部"
              >
                <Option value="">全部</Option>
                <Option value="user">普通用户</Option>
                <Option value="npc">NPC</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={6}>
            <div>
              <Text strong style={{ marginRight: 8 }}>状态筛选:</Text>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: '100%', maxWidth: 150 }}
                allowClear
                placeholder="选择状态"
              >
                <Option value="">全部</Option>
                <Option value="active">激活</Option>
                <Option value="inactive">禁用</Option>
                <Option value="verified">已认证</Option>
                <Option value="pending">待审核</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={6}>
            <div>
              <Text strong style={{ marginRight: 8 }}>角色筛选:</Text>
              <Select
                value={roleFilter}
                onChange={setRoleFilter}
                style={{ width: '100%', maxWidth: 200 }}
                allowClear
                placeholder="选择角色"
                loading={rolesLoading}
                optionLabelProp="label"
              >
                <Option value="" label="全部角色">全部角色</Option>
                {roles.map((role) => {
                  // 从 role_code 提取前缀和分类
                  const prefix = role.prefix || role.role_code?.split('_')[0] || 'unknown'
                  const name = role.name || role.role_name || role.role_code
                  const category = role.category || '其他'
                  return (
                    <Option
                      key={role.role_code}
                      value={role.role_code}
                      label={`[${prefix}_${role.role_code}] ${name}`}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Tag color="blue">{prefix}</Tag>
                        <span>{name}</span>
                        {role.category && (
                          <Text type="secondary" style={{ fontSize: 11, marginLeft: 'auto' }}>
                            {category}
                          </Text>
                        )}
                      </div>
                    </Option>
                  )
                })}
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={6}>
            <div className="flex items-center gap-4">
              <Text type="secondary">
                共 {users.length} 个用户
              </Text>
              <div className="flex items-center gap-2">
                <Switch
                  size="small"
                  checked={includeDeleted}
                  onChange={setIncludeDeleted}
                />
                <span className="text-sm text-gray-500">显示已删除</span>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => {
            setPage(page)
            setPageSize(pageSize || 20)
          },
        }}
      />

      <Modal
        title={
          <span>
            {editingUser ? '编辑用户' : '添加用户'}
            {editingUser && (
              <Text type="secondary" className="ml-3 text-sm">
                ID: {editingUser.id}
              </Text>
            )}
          </span>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={isMobile ? '90%' : 600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ is_active: true }}
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>

          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' },
            ]}
          >
            <Input placeholder="请输入邮箱" />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label="密码"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码至少6个字符' },
              ]}
            >
              <Input.Password placeholder="请输入密码" />
            </Form.Item>
          )}

          <Form.Item
            name="full_name"
            label="姓名"
          >
            <Input placeholder="请输入姓名" />
          </Form.Item>

          {/* Login Methods Binding Status */}
          {editingUser && (
            <Form.Item label="登录方式绑定状态">
              <Card size="small">
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {/* Email Status */}
                  <div className="flex items-center justify-between">
                    <Space>
                      <MailOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                      <div>
                        <Text strong style={{ fontSize: 13 }}>邮箱</Text>
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {editingUser.email || '未绑定'}
                          </Text>
                          {editingUser.is_verified && (
                            <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: 8 }} />
                          )}
                        </div>
                      </div>
                    </Space>
                    <Space>
                      <Tag color={editingUser.email ? (editingUser.is_verified ? 'success' : 'warning') : 'default'}>
                        {editingUser.email ? (editingUser.is_verified ? '已验证' : '未验证') : '未绑定'}
                      </Tag>
                      {editingUser.email ? (
                        <Popconfirm
                          title="确认解绑邮箱"
                          description="确定要解绑此邮箱吗？"
                          onConfirm={() => handleUnbindEmail(editingUser)}
                          okText="确认"
                          cancelText="取消"
                        >
                          <Button size="small" danger type="link">
                            解绑
                          </Button>
                        </Popconfirm>
                      ) : (
                        <Button
                          size="small"
                          type="primary"
                          onClick={() => {
                            setEditingUser(editingUser)
                            setBindEmailModalVisible(true)
                          }}
                        >
                          绑定
                        </Button>
                      )}
                    </Space>
                  </div>

                  <Divider style={{ margin: '8px 0' }} />

                  {/* Phone Status */}
                  <div className="flex items-center justify-between">
                    <Space>
                      <PhoneOutlined style={{ fontSize: 18, color: '#52c41a' }} />
                      <div>
                        <Text strong style={{ fontSize: 13 }}>手机</Text>
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {editingUser.phone || '未绑定'}
                          </Text>
                        </div>
                      </div>
                    </Space>
                    <Space>
                      <Tag color={editingUser.phone ? 'success' : 'default'}>
                        {editingUser.phone ? '已绑定' : '未绑定'}
                      </Tag>
                      {editingUser.phone ? (
                        <Popconfirm
                          title="确认解绑手机"
                          description="确定要解绑此手机号吗？"
                          onConfirm={() => handleUnbindPhone(editingUser)}
                          okText="确认"
                          cancelText="取消"
                        >
                          <Button size="small" danger type="link">
                            解绑
                          </Button>
                        </Popconfirm>
                      ) : (
                        <Button
                          size="small"
                          type="primary"
                          onClick={() => {
                            setEditingUser(editingUser)
                            setBindPhoneModalVisible(true)
                          }}
                        >
                          绑定
                        </Button>
                      )}
                    </Space>
                  </div>

                  <Divider style={{ margin: '8px 0' }} />

                  {/* WeChat Status */}
                  <div className="flex items-center justify-between">
                    <Space>
                      <WechatOutlined style={{ fontSize: 18, color: '#07c160' }} />
                      <div>
                        <Text strong style={{ fontSize: 13 }}>微信</Text>
                        <div>
                          {editingUser.wechat_union_id ? (
                            <Space direction="vertical" size={2}>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {editingUser.wechat_nickname || '微信用户'}
                              </Text>
                              {editingUser.wechat_bound_at && (
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                  {new Date(editingUser.wechat_bound_at).toLocaleDateString('zh-CN')}
                                </Text>
                              )}
                            </Space>
                          ) : (
                            <Text type="secondary" style={{ fontSize: 12 }}>未绑定</Text>
                          )}
                        </div>
                      </div>
                    </Space>
                    <Space>
                      <Tag color={editingUser.wechat_union_id ? 'success' : 'default'}>
                        {editingUser.wechat_union_id ? '已绑定' : '未绑定'}
                      </Tag>
                      {editingUser.wechat_union_id ? (
                        <Popconfirm
                          title="确认解绑微信"
                          description="确定要解绑此微信账号吗？"
                          onConfirm={() => handleUnbindWeChat(editingUser)}
                          okText="确认"
                          cancelText="取消"
                        >
                          <Button size="small" danger type="link">
                            解绑
                          </Button>
                        </Popconfirm>
                      ) : (
                        <Button
                          size="small"
                          type="primary"
                          onClick={() => setBindWechatModalVisible(true)}
                        >
                          绑定
                        </Button>
                      )}
                    </Space>
                  </div>
                </Space>
              </Card>
            </Form.Item>
          )}

          <Form.Item name="avatar_url" label="用户头像">
            <div className="space-y-2">
              <ImageUpload
                value={avatarUrl}
                onChange={(url) => {
                  setAvatarUrl(url)
                  form.setFieldValue('avatar_url', url)
                }}
              />
              {avatarUrl && (
                <Button
                  size="small"
                  danger
                  onClick={() => {
                    setAvatarUrl('')
                    form.setFieldValue('avatar_url', undefined)
                  }}
                >
                  移除头像
                </Button>
              )}
            </div>
          </Form.Item>

          {/* Extended Profile Fields */}
          <Divider orientation="left">扩展信息</Divider>

          <Form.Item
            name="bio"
            label="个人介绍"
          >
            <Input.TextArea placeholder="请输入个人介绍" rows={3} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="gender"
                label="性别"
              >
                <Select placeholder="请选择性别">
                  <Option value="male">男</Option>
                  <Option value="female">女</Option>
                  <Option value="other">其他</Option>
                  <Option value="prefer_not_to_say">不愿透露</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="birthday"
                label="生日"
              >
                <Input type="date" placeholder="请选择生日" />
              </Form.Item>
            </Col>


            <Col span={12}><Form.Item
              name="region"
              label="地区"
            >
              <Input placeholder="请输入所在地区" />
            </Form.Item>

              <Form.Item
                name="profession"
                label="职业"
              >
                <Input placeholder="请输入职业" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="personality_tags"
                label="性格标签"
              >
                <Select
                  mode="tags"
                  placeholder="请选择或输入性格标签"
                  tokenSeparators={[',']}
                  loading={tagsLoading}
                  options={(personalityTagOptions || []).map(tag => ({
                    value: tag.value,
                    label: tag.label,
                  }))}
                />
              </Form.Item>

              <Form.Item
                name="interest_tags"
                label="兴趣爱好"
              >
                <Select
                  mode="tags"
                  placeholder="请选择或输入兴趣爱好标签"
                  tokenSeparators={[',']}
                  loading={tagsLoading}
                  options={(interestTagOptions || []).map(tag => ({
                    value: tag.value,
                    label: tag.label,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="role"
                label="角色"
                rules={[{ required: true, message: '请选择角色' }]}
              >
                <Select
                  placeholder="请选择角色"
                  loading={rolesLoading}
                  showSearch
                  optionFilterProp="label"
                  optionLabelProp="label"
                >
                  {roles.map((role) => {
                    // 从 role_code 提取前缀和分类
                    const prefix = role.prefix || role.role_code?.split('_')[0] || 'unknown'
                    const name = role.name || role.role_name || role.role_code
                    const category = role.category || '其他'
                    return (
                      <Option
                        key={role.role_code}
                        value={role.role_code}
                        label={`[${prefix}_${role.role_code}] ${name}`}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Tag color="blue">{prefix}</Tag>
                          <span>{name}</span>
                          {role.category && (
                            <Text type="secondary" style={{ fontSize: 11, marginLeft: 'auto' }}>
                              {category}
                            </Text>
                          )}
                        </div>
                      </Option>
                    )
                  })}
                </Select>
              </Form.Item>

              <Form.Item
                name="is_active"
                label="状态"
                valuePropName="checked"
              >
                <Switch checkedChildren="激活" unCheckedChildren="禁用" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingUser ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 用户详情模态框 - Enhanced */}
      <Modal
        title="用户详情"
        open={profileModalVisible}
        onCancel={() => setProfileModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setProfileModalVisible(false)}>
            关闭
          </Button>,
          <Button
            key="edit"
            type="primary"
            onClick={() => {
              setProfileModalVisible(false)
              if (viewingUser) handleEdit(viewingUser)
            }}
          >
            编辑用户
          </Button>
        ]}
        width={700}
      >
        {viewingUser && (
          <div className="space-y-4">
            {/* User Avatar and Basic Info */}
            <div className="flex items-center space-x-4">
              {viewingUser.avatar_url || viewingUser.wechat_avatar_url ? (
                <Avatar
                  size={80}
                  src={viewingUser.avatar_url || viewingUser.wechat_avatar_url}
                />
              ) : (
                <Avatar size={80} icon={<UserOutlined />} />
              )}
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  {viewingUser.full_name || viewingUser.username}
                </Title>
                <Text type="secondary">@{viewingUser.username}</Text>
                {viewingUser.is_superuser && (
                  <Tag color="red" style={{ marginLeft: 8 }}>超级管理员</Tag>
                )}
              </div>
            </div>

            <Divider />

            {/* Login Methods Section */}
            <div>
              <Title level={5}>登录方式绑定</Title>
              <Card size="small" className="mt-2">
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {/* Email Binding */}
                  <div className="flex items-center justify-between">
                    <Space>
                      <MailOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                      <div>
                        <Text strong>邮箱登录</Text>
                        <div>
                          {viewingUser.email ? (
                            <>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {viewingUser.email}
                              </Text>
                              {viewingUser.is_verified && (
                                <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: 8 }} />
                              )}
                            </>
                          ) : (
                            <Text type="secondary" style={{ fontSize: 12 }}>未绑定</Text>
                          )}
                        </div>
                      </div>
                    </Space>
                    <Space>
                      {viewingUser.email ? (
                        <Tag color={viewingUser.is_verified ? 'success' : 'warning'}>
                          {viewingUser.is_verified ? '已验证' : '未验证'}
                        </Tag>
                      ) : (
                        <Tag color="default">未绑定</Tag>
                      )}
                      <Button
                        size="small"
                        type="link"
                        onClick={() => setBindEmailModalVisible(true)}
                      >
                        {viewingUser.email ? '重新绑定' : '绑定'}
                      </Button>
                    </Space>
                  </div>

                  <Divider style={{ margin: '8px 0' }} />

                  {/* Phone Binding */}
                  <div className="flex items-center justify-between">
                    <Space>
                      <PhoneOutlined style={{ fontSize: 20, color: '#52c41a' }} />
                      <div>
                        <Text strong>手机登录</Text>
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {viewingUser.phone || '未绑定'}
                          </Text>
                        </div>
                      </div>
                    </Space>
                    <Space>
                      <Tag color={viewingUser.phone ? 'success' : 'default'}>
                        {viewingUser.phone ? '已绑定' : '未绑定'}
                      </Tag>
                      {viewingUser.phone ? (
                        <Popconfirm
                          title="确认解绑手机"
                          description="确定要解绑此用户的手机号吗？"
                          onConfirm={() => handleUnbindPhone(viewingUser)}
                          okText="确认"
                          cancelText="取消"
                        >
                          <Button size="small" danger type="link">
                            解绑
                          </Button>
                        </Popconfirm>
                      ) : (
                        <Button
                          size="small"
                          type="link"
                          onClick={() => setBindPhoneModalVisible(true)}
                        >
                          绑定
                        </Button>
                      )}
                    </Space>
                  </div>

                  <Divider style={{ margin: '8px 0' }} />

                  {/* WeChat Binding */}
                  <div className="flex items-center justify-between">
                    <Space>
                      <WechatOutlined style={{ fontSize: 20, color: '#07c160' }} />
                      <div>
                        <Text strong>微信登录</Text>
                        <div>
                          {viewingUser.wechat_union_id ? (
                            <Space>
                              {viewingUser.wechat_avatar_url && (
                                <Avatar size={24} src={viewingUser.wechat_avatar_url} />
                              )}
                              <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {viewingUser.wechat_nickname || '微信用户'}
                                </Text>
                                {viewingUser.wechat_bound_at && (
                                  <div>
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                      绑定于: {new Date(viewingUser.wechat_bound_at).toLocaleString('zh-CN')}
                                    </Text>
                                  </div>
                                )}
                              </div>
                            </Space>
                          ) : (
                            <Text type="secondary" style={{ fontSize: 12 }}>未绑定</Text>
                          )}
                        </div>
                      </div>
                    </Space>
                    <Space>
                      <Tag color={viewingUser.wechat_union_id ? 'success' : 'default'}>
                        {viewingUser.wechat_union_id ? '已绑定' : '未绑定'}
                      </Tag>
                      {viewingUser.wechat_union_id && (
                        <Popconfirm
                          title="确认解绑微信"
                          description="确定要解绑此用户的微信账号吗？"
                          onConfirm={() => handleUnbindWeChat(viewingUser)}
                          okText="确认"
                          cancelText="取消"
                        >
                          <Button size="small" danger type="link">
                            解绑
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  </div>
                </Space>
              </Card>
            </div>

            <Divider />

            {/* Account Information */}
            <Row gutter={16}>
              <Col span={12}>
                <div className="mb-4">
                  <Text type="secondary">角色</Text>
                  <div>
                    {(() => {
                      const roleConfig: Record<string, { color: string; name: string; prefix: string }> = {
                        super_admin: { color: 'red', name: '超级管理员', prefix: 'admin' },
                        platform_admin: { color: 'red', name: '平台管理员', prefix: 'admin' },
                        admin: { color: 'volcano', name: '管理员', prefix: 'admin' },
                        merchant_admin: { color: 'orange', name: '商家管理员', prefix: 'admin' },
                        venue_admin: { color: 'gold', name: '场馆管理员', prefix: 'admin' },
                        playwright: { color: 'blue', name: '编剧', prefix: 'content' },
                        reviewer: { color: 'cyan', name: '审核员', prefix: 'content' },
                        merchant: { color: 'green', name: '商家', prefix: 'merchant' },
                        actor: { color: 'purple', name: '演员', prefix: 'actor' },
                        advertiser: { color: 'magenta', name: '广告主', prefix: 'ads' },
                        agent_developer: { color: 'geekblue', name: 'Agent开发者', prefix: 'dev' },
                        player: { color: 'default', name: '玩家', prefix: 'user' },
                      }
                      const roleCode = viewingUser.role_code || (viewingUser.roles && viewingUser.roles[0]) || viewingUser.role || 'unknown'
                      const config = roleConfig[roleCode] || { color: 'default', name: roleCode, prefix: 'unknown' }
                      return (
                        <Tag color={config.color}>
                          <span style={{ fontSize: 11, opacity: 0.7, marginRight: 4 }}>[{config.prefix}]</span>
                          {config.name}
                        </Tag>
                      )
                    })()}
                  </div>
                </div>
                <div className="mb-4">
                  <Text type="secondary">账号状态</Text>
                  <div>
                    <Tag color={viewingUser.is_active ? 'green' : 'red'}>
                      {viewingUser.is_active ? '激活' : '禁用'}
                    </Tag>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div className="mb-4">
                  <Text type="secondary">KYC状态</Text>
                  <div>
                    <Tag color={
                      viewingUser.kyc_status === 'approved' ? 'green' :
                        viewingUser.kyc_status === 'pending' ? 'orange' : 'red'
                    }>
                      {viewingUser.kyc_status === 'approved' ? '已通过' :
                        viewingUser.kyc_status === 'pending' ? '待审核' :
                          viewingUser.kyc_status ? '已拒绝' : '未提交'}
                    </Tag>
                  </div>
                </div>
                <div className="mb-4">
                  <Text type="secondary">最后登录</Text>
                  <div>
                    <Text>
                      {viewingUser.last_login_at
                        ? new Date(viewingUser.last_login_at).toLocaleString('zh-CN')
                        : '从未登录'}
                    </Text>
                  </div>
                </div>
              </Col>
            </Row>

            <Divider />

            {/* Registration Time */}
            <div className="mb-4">
              <Text type="secondary">注册时间</Text>
              <div><Text>{new Date(viewingUser.created_at).toLocaleString('zh-CN')}</Text></div>
            </div>

            {/* Special Permissions */}
            {(viewingUser.is_superuser || viewingUser.is_staff) && (
              <>
                <Divider />
                <div className="mb-4">
                  <Text type="secondary">特殊权限</Text>
                  <div className="mt-2 space-x-2">
                    {viewingUser.is_superuser && <Tag color="red">超级用户</Tag>}
                    {viewingUser.is_staff && <Tag color="orange">员工账号</Tag>}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Bind Email Modal */}
      <BindModal
        visible={bindEmailModalVisible}
        onCancel={() => setBindEmailModalVisible(false)}
        title="绑定邮箱"
        onSubmit={handleBindEmail}
        onSendCode={handleSendVerificationCode}
        loading={bindingLoading}
      />

      {/* Bind Phone Modal */}
      <BindModal
        visible={bindPhoneModalVisible}
        onCancel={() => setBindPhoneModalVisible(false)}
        title="绑定手机"
        onSubmit={handleBindPhone}
        onSendCode={handleSendVerificationCode}
        loading={bindingLoading}
      />

      {/* Bind WeChat Modal */}
      <WeChatBindModal
        visible={bindWechatModalVisible}
        onCancel={() => setBindWechatModalVisible(false)}
        onSubmit={handleBindWeChat}
        loading={bindingLoading}
      />

      {/* 设置密码模态框 */}
      <Modal
        title={
          <span>
            设置密码
            {passwordUser && (
              <Text type="secondary" className="ml-3 text-sm">
                用户: {passwordUser.username}
              </Text>
            )}
          </span>
        }
        open={passwordModalVisible}
        onCancel={() => setPasswordModalVisible(false)}
        onOk={handleSetPasswordConfirm}
        confirmLoading={passwordLoading}
        okText="确认"
        cancelText="取消"
        okButtonProps={{ disabled: !newPassword || newPassword.length < 6 }}
      >
        <Form layout="vertical">
          <Form.Item
            label="新密码"
            required
            help="密码至少6个字符"
          >
            <Input.Password
              placeholder="请输入新密码（至少6个字符）"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onPressEnter={handleSetPasswordConfirm}
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 绑定场馆弹窗 */}
      <Modal
        title={
          <Space>
            <HomeOutlined />
            <span>绑定场馆信息</span>
          </Space>
        }
        open={venueBindModalVisible}
        onCancel={() => {
          setVenueBindModalVisible(false)
          venueBindForm.resetFields()
          setSelectedMerchantId(null)
          setVenues([])
        }}
        footer={null}
        width={600}
      >
        <Alert
          message="场馆绑定说明"
          description={
            <div>
              <p>用户: <strong>{bindingVenueUser?.username}</strong></p>
              <p>为该场馆管理员绑定商户和场馆信息后，用户可登录场馆监控大屏查看对应场馆数据。</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form
          form={venueBindForm}
          layout="vertical"
          onFinish={handleSubmitVenueBind}
        >
          <Form.Item
            label="选择商户"
            name="merchant_id"
            rules={[{ required: true, message: '请选择商户' }]}
          >
            <Select
              placeholder="请选择商户"
              loading={merchantsLoading}
              onChange={handleMerchantChange}
              showSearch
              optionFilterProp="children"
            >
              {merchants.map((merchant) => (
                <Option key={merchant.id} value={merchant.id}>
                  [{merchant.id}] {merchant.company_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="选择场馆"
            name="venue_id"
            rules={[{ required: true, message: '请选择场馆' }]}
          >
            <Select
              placeholder={selectedMerchantId ? "请选择场馆" : "请先选择商户"}
              loading={venuesLoading}
              disabled={!selectedMerchantId}
              showSearch
              optionFilterProp="children"
            >
              {venues.map((venue) => (
                <Option key={venue.id} value={venue.id}>
                  [{venue.id}] {venue.venue_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {selectedMerchantId && venues.length === 0 && !venuesLoading && (
            <Alert
              message="该商户暂无场馆"
              description="请先在商户管理中为该商户创建场馆。"
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setVenueBindModalVisible(false)
                  venueBindForm.resetFields()
                  setSelectedMerchantId(null)
                  setVenues([])
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                确认绑定
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

// Bind Modal Component
interface BindModalProps {
  visible: boolean
  onCancel: () => void
  title: string
  onSubmit: (value: string, code: string) => void
  onSendCode: (type: 'email' | 'phone', target: string) => Promise<boolean>
  loading?: boolean
}

const BindModal: React.FC<BindModalProps> = ({
  visible,
  onCancel,
  title,
  onSubmit,
  onSendCode,
  loading
}) => {
  const [form] = Form.useForm()
  const [codeSent, setCodeSent] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [inputValue, setInputValue] = useState('')

  const isEmail = title.includes('邮箱')
  const inputPlaceholder = isEmail ? '请输入邮箱地址' : '请输入手机号'
  const inputType = isEmail ? 'email' : 'tel'

  const handleSendCode = async () => {
    if (!inputValue) {
      message.error(`请先输入${isEmail ? '邮箱' : '手机号'}`)
      return
    }

    const success = await onSendCode(isEmail ? 'email' : 'phone', inputValue)
    if (success) {
      setCodeSent(true)
      setCountdown(60)
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
  }

  const handleSubmit = () => {
    const code = form.getFieldValue('code')
    if (!inputValue) {
      message.error(`请先输入${isEmail ? '邮箱' : '手机号'}`)
      return
    }
    if (!code) {
      message.error('请输入验证码')
      return
    }
    onSubmit(inputValue, code)
  }

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText="绑定"
      cancelText="取消"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={isEmail ? '邮箱地址' : '手机号'}
          required
        >
          <Input
            type={inputType}
            placeholder={inputPlaceholder}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            addonAfter={
              <Button
                size="small"
                type="link"
                disabled={!inputValue || countdown > 0}
                onClick={handleSendCode}
              >
                {countdown > 0 ? `${countdown}s` : '发送验证码'}
              </Button>
            }
          />
        </Form.Item>

        <Form.Item
          label="验证码"
          name="code"
          required
        >
          <Input
            placeholder="请输入6位验证码"
            maxLength={6}
          />
        </Form.Item>

        {codeSent && (
          <div style={{ marginTop: 16, padding: 12, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {isEmail ? '验证码已发送到您的邮箱' : '验证码已发送到您的手机'}
              {countdown > 0 && `，请在 ${countdown} 秒内完成验证`}
            </Text>
          </div>
        )}
      </Form>
    </Modal>
  )
}

// WeChat Bind Modal Component
interface WeChatBindModalProps {
  visible: boolean
  onCancel: () => void
  onSubmit: (unionId: string, nickname?: string, avatarUrl?: string) => void
  loading?: boolean
}

const WeChatBindModal: React.FC<WeChatBindModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  loading
}) => {
  const [form] = Form.useForm()
  const [unionId, setUnionId] = useState('')
  const [nickname, setNickname] = useState('')
  const [avatarUrl, setAvatarUrl] = useState('')

  const handleSubmit = () => {
    if (!unionId) {
      message.error('请输入微信 UnionID')
      return
    }
    onSubmit(unionId, nickname, avatarUrl)
  }

  const handleUnionIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUnionId(e.target.value)
  }

  const handleNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNickname(e.target.value)
  }

  const handleAvatarUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAvatarUrl(e.target.value)
  }

  // Reset form when modal closes
  useEffect(() => {
    if (!visible) {
      setUnionId('')
      setNickname('')
      setAvatarUrl('')
      form.resetFields()
    }
  }, [visible, form])

  return (
    <Modal
      title="绑定微信"
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText="绑定"
      cancelText="取消"
    >
      <Form form={form} layout="vertical">
        <Alert
          message="微信绑定说明"
          description={
            <>
              <p>请从微信开放平台获取用户的 UnionID，然后在此处绑定。</p>
              <p style={{ marginBottom: 0 }}>
                UnionID 格式示例：<code>ouIidk9xKx8...</code>
              </p>
            </>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form.Item
          label="微信 UnionID"
          required
        >
          <Input
            placeholder="请输入微信 UnionID"
            value={unionId}
            onChange={handleUnionIdChange}
          />
        </Form.Item>

        <Form.Item label="微信昵称">
          <Input
            placeholder="可选，请输入微信昵称"
            value={nickname}
            onChange={handleNicknameChange}
          />
        </Form.Item>

        <Form.Item label="微信头像 URL">
          <Input
            placeholder="可选，请输入头像 URL"
            value={avatarUrl}
            onChange={handleAvatarUrlChange}
          />
        </Form.Item>

        {avatarUrl && (
          <div style={{ marginTop: 16 }}>
            <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
              头像预览:
            </Text>
            <Avatar size={64} src={avatarUrl} icon={<UserOutlined />} />
          </div>
        )}
      </Form>
    </Modal>
  )
}

export default UserManagement
